import { useQuery } from "@tanstack/react-query";
import { evaluateDay, scheduledMinutesFor, type DayPunch, type DayResult } from "@core/day.ts";
import { holidaysByDay } from "@core/holidays.ts";
import { rulesForDay } from "@core/rules.ts";
import { addDays, localDateOf, weekdayIndex } from "@core/time.ts";
import { useTenant } from "@/lib/auth";
import { useEmployees, useRuleSets, useTenantRow, type Employee } from "@/lib/data";
import { qk } from "@/lib/query-keys";
import { supabase } from "@/lib/supabase";

export interface UnassignedCard {
  actorId: string;
  actorName: string | null;
  count: number;
  lastAt: string;
  deviceId: string | null;
}

/** Karten-Eingang: Ereignisse unbekannter Karten, nach Karte gruppiert. */
export function useUnassignedEvents({ enabled = true }: { enabled?: boolean } = {}) {
  const { tenant_id } = useTenant();
  return useQuery({
    queryKey: qk.unassignedEvents(tenant_id),
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("terminal_events")
        .select("external_actor_id, external_actor_name, external_device_id, received_at")
        .eq("processing_status", "unassigned")
        .order("received_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      const byActor = new Map<string, UnassignedCard>();
      for (const e of data ?? []) {
        if (!e.external_actor_id) continue;
        const card = byActor.get(e.external_actor_id);
        if (card) card.count++;
        else
          byActor.set(e.external_actor_id, {
            actorId: e.external_actor_id,
            actorName: e.external_actor_name,
            count: 1,
            lastAt: e.received_at,
            deviceId: e.external_device_id,
          });
      }
      return [...byActor.values()];
    },
  });
}

export interface TodayRow {
  employee: Employee;
  result: DayResult;
  firstIn: string | null;
  scheduled: number;
  absenceType: string | null;
  holiday: string | null;
  status: "present" | "break" | "gone" | "not_started" | "absent" | "day_off" | "holiday";
  /** Eingestempelt, ohne ausreichende Pause und höchstens noch eine Stunde bis zur Schwelle. */
  breakDue: boolean;
}

/**
 * Heutiger Stand aller Mitarbeiter — live mit derselben Zeitlogik wie der Server
 * (evaluateDay, offene Sitzung zählt bis jetzt).
 */
export function useToday() {
  const { tenant_id, timezone } = useTenant();
  const employees = useEmployees();
  const ruleSets = useRuleSets();
  const tenantRow = useTenantRow();
  const today = localDateOf(new Date(), timezone);

  const data = useQuery({
    queryKey: qk.punchesOfDay(tenant_id, today),
    refetchInterval: 30_000,
    queryFn: async () => {
      const [punches, assignments, absences, anomalies] = await Promise.all([
        supabase.from("punches").select("id, employee_id, occurred_at, status").eq("local_date", today).order("occurred_at"),
        supabase
          .from("employee_schedule_assignments")
          .select("employee_id, schedule_templates(weekday_minutes)")
          .lte("valid_from", today)
          .or(`valid_to.is.null,valid_to.gte.${today}`),
        supabase.from("absences").select("employee_id, type, minutes_per_day").lte("starts_on", today).gte("ends_on", today),
        supabase
          .from("work_days")
          .select("employee_id, work_date, anomalies")
          .gte("work_date", addDays(today, -31))
          .lt("work_date", today)
          .is("anomalies_acknowledged_at", null)
          .neq("anomalies", "[]"),
      ]);
      for (const r of [punches, assignments, absences, anomalies]) if (r.error) throw r.error;
      return {
        punches: punches.data as { id: string; employee_id: string; occurred_at: string; status: DayPunch["status"] }[],
        assignments: assignments.data as unknown as { employee_id: string; schedule_templates: { weekday_minutes: number[] } | null }[],
        absences: absences.data as { employee_id: string; type: string; minutes_per_day: number | null }[],
        openAnomalyDays: (anomalies.data as { anomalies: { severity: string }[] }[]).filter((d) =>
          d.anomalies.some((a) => a.severity === "warning"),
        ).length,
      };
    },
  });

  const now = new Date().toISOString();
  let rows: TodayRow[] = [];
  if (data.data && employees.data && ruleSets.data && tenantRow.data) {
    const rules = rulesForDay(ruleSets.data.map((r) => ({ validFrom: r.valid_from, rules: r.rules })), today);
    const holiday = holidaysByDay(today, today, tenantRow.data.holiday_regions).get(today);
    rows = employees.data
      .filter((e) => (!e.entry_date || e.entry_date <= today) && (!e.exit_date || e.exit_date >= today))
      .map((employee) => {
        const punches = data.data.punches
          .filter((p) => p.employee_id === employee.id)
          .map((p) => ({ id: p.id, occurredAt: p.occurred_at, status: p.status }));
        const assignment = data.data.assignments.find((a) => a.employee_id === employee.id);
        const scheduled = scheduledMinutesFor(assignment?.schedule_templates?.weekday_minutes, weekdayIndex(today));
        const absence = data.data.absences.find((a) => a.employee_id === employee.id) ?? null;
        const result = evaluateDay({
          punches,
          rules,
          scheduledMinutes: scheduled,
          holidayName: holiday?.name.de ?? null,
          absence: absence ? { type: absence.type as never, minutesPerDay: absence.minutes_per_day } : null,
          closing: false,
          countOpenUntil: now,
        });
        const counted = result.countedPunchIds.length;
        const status: TodayRow["status"] = result.present
          ? "present"
          : counted > 0
            ? counted % 2 === 0 && new Date().getTime() - Date.parse(result.intervals.at(-1)?.end ?? now) < 90 * 60_000
              ? "break"
              : "gone"
            : absence && absence.minutes_per_day === null
              ? "absent"
              : holiday
                ? "holiday"
                : scheduled === 0
                  ? "day_off"
                  : "not_started";
        return {
          employee,
          result,
          firstIn: result.intervals[0]?.start ?? null,
          scheduled,
          absenceType: absence?.type ?? null,
          holiday: holiday?.name.de ?? null,
          status,
          breakDue:
            result.present &&
            rules.requiredBreakMinutes > 0 &&
            result.stampedBreakMinutes < rules.requiredBreakMinutes &&
            result.grossMinutes >= rules.breakThresholdMinutes - 60,
        };
      });
  }

  return {
    today,
    rows,
    openAnomalyDays: data.data?.openAnomalyDays ?? 0,
    isLoading: data.isLoading || employees.isLoading || ruleSets.isLoading || tenantRow.isLoading,
    error: data.error ?? employees.error ?? ruleSets.error ?? tenantRow.error,
    refetch: data.refetch,
  };
}
